import type { AxiosResponse } from 'axios';
import { createClient } from './graphql';
import type {
  DeleteAccountSuccess,
  DeleteAccountFailure,
} from '../modules/graphql/types';
import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import type { Zone } from '@seek/audience-zones';

interface ApiOptions {
  mockAuthOptions?: {
    authenticated: boolean;
  };
  useMocks: boolean;
  isServer?: boolean;
  zone: Zone;
}

export interface ApiInterface {
  getUser(): Promise<any>;
  deleteAccount({
    password,
  }: {
    password: string;
  }): Promise<DeleteAccountSuccess | DeleteAccountFailure>;
  changeEmail({
    newEmail,
    currentPassword,
  }: {
    newEmail: string;
    currentPassword: string;
  }): Promise<AxiosResponse>;
  changePassword(
    {
      newPassword,
      confirmPassword,
      currentPassword,
    }: {
      newPassword: string;
      confirmPassword: string;
      currentPassword: string;
    },
    emailAddress: string,
  ): Promise<AxiosResponse>;
  graphqlClient: ApolloClient<NormalizedCacheObject>;
}

export default ({ useMocks, mockAuthOptions, isServer, zone }: ApiOptions) => {
  const appName = 'SEEK Settings';

  const authOptions = {
    appName,
    useMockedAuth: useMocks,
    mockOptions: mockAuthOptions,
    useAuth: true,
    forceStrategy: 'AUTH0',
  };

  const graphqlClient = createClient(
    authOptions,
    appName,
    useMocks,
    zone,
    isServer,
  );
  const api: ApiInterface = {
    getUser: async () => graphqlClient.getUser(),
    changeEmail: async ({ newEmail, currentPassword }) =>
      graphqlClient.changeEmail({
        newEmailAddress: newEmail,
        password: currentPassword,
      }),
    changePassword: ({ newPassword, currentPassword }) =>
      graphqlClient.changePassword({
        newPassword,
        password: currentPassword,
      }),
    deleteAccount: ({ password }) =>
      graphqlClient.deleteAccount({
        password,
      }),
    graphqlClient: graphqlClient.client,
  };

  return api;
};
